import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ArrowRight, Loader2, MessageCircle } from "lucide-react";
import { api, whatsappLink } from "@/lib/api";
import { usePageImages } from "@/v2/lib/usePageImages";

const STEPS = [
  { n: "01", t: "Share your idea", d: "Fill in the enquiry below with your size, palette and inspiration — or send us a WhatsApp message." },
  { n: "02", t: "Concept & quote", d: "Our design team returns a colour swatch, motif direction and a transparent quote within 48 hours." },
  { n: "03", t: "Approve the design", d: "You review a woven-ready artwork and colour proof. We only begin once you say yes." },
  { n: "04", t: "Handcrafted", d: "Your rug is knotted / tufted by a small team of Bhadohi weavers. Monthly progress photos, always." },
  { n: "05", t: "Quality check", d: "Every piece is washed, clipped and inspected before it leaves the atelier." },
  { n: "06", t: "Delivered home", d: "Fully insured shipping to your door — plus a personal handover if you're in Bengaluru." },
];

const SIZES = ["3 × 5 ft", "4 × 6 ft", "5 × 7 ft", "5 × 8 ft", "6 × 9 ft", "8 × 10 ft", "Custom / other"];
const MATERIALS = ["Wool", "Silk-wool blend", "Cotton", "Jute", "Chenille", "Not sure yet"];
const ROOMS = ["Living Room", "Bedroom", "Dining Area", "Entryway", "Kids' Room", "Office", "Other"];
const BUDGETS = ["Under ₹15,000", "₹15,000 – ₹30,000", "₹30,000 – ₹60,000", "₹60,000 – ₹1,20,000", "Above ₹1,20,000", "Not sure yet"];

export default function V2Custom() {
  const fileRef = useRef(null);
  const { img } = usePageImages();
  const [f, setF] = useState({
    name: "", email: "", phone: "", city_country: "", rug_size: "",
    preferred_colors: "", material: "", room_type: "", budget_range: "", required_by: "", notes: "",
  });
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) return toast.error("Please upload an image under 4MB");
    const r = new FileReader();
    r.onload = () => { setFile((r.result || "").toString().split(",")[1] || null); setFileName(f.name); };
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) return toast.error("Please share your name, email and phone");
    setLoading(true);
    try {
      await api.post("/custom-rug-enquiry", { ...f, inspiration_image_base64: file });
      setSent(true);
      toast.success("Thank you — we'll write back within one working day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="v2-custom-page">
      {/* Hero */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <div className="overline-v2">The Custom Studio</div>
          <h1 className="mt-5 font-serif-warm text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
            A rug created for<br/><em>your space.</em>
          </h1>
          <p className="mt-8 text-[16px] text-[#4a4640] leading-relaxed font-light max-w-xl">
            Choose your size, colours and design, or share an inspiration image with us. Our team will help transform your idea into a rug handcrafted especially for your home.
          </p>
        </div>
        <div className="lg:col-span-6">
          <img src={img("custom.hero", "https://images.unsplash.com/photo-1615529182904-14819c35db37?auto=format&fit=crop&w=1400&q=85")} alt="Custom rug" className="w-full aspect-[4/5] object-cover" />
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#f2ede2]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-20">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="overline-v2">Our process</div>
            <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.05]">From idea to heirloom, in six unhurried steps.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="border-t border-[#1a1a1a] pt-5">
                <div className="font-serif-warm text-3xl text-[#b85742]">{s.n}</div>
                <div className="mt-3 font-serif-warm text-2xl">{s.t}</div>
                <p className="mt-3 text-[14px] text-[#4a4640] leading-relaxed font-light">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquiry" className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline-v2">Start your rug</div>
          <h2 className="mt-4 font-serif-warm text-4xl md:text-5xl leading-[1.05]">Tell us the shape of your idea.</h2>
          <p className="mt-6 text-[15px] text-[#4a4640] leading-relaxed font-light max-w-md">
            The more you can share — a colour, a photograph, the room it will live in — the closer our first sketch will be to what you have in mind.
          </p>
          <a href={whatsappLink("Hi Iryaa Homes, I'd like to commission a custom rug.")} target="_blank" rel="noreferrer" className="mt-8 v2-btn-outline">
            <MessageCircle size={13} /> Chat on WhatsApp
          </a>
        </div>

        <form onSubmit={submit} data-testid="v2-custom-form" className="lg:col-span-7 border border-[#e4ded2] bg-[#fbf9f5] p-8 md:p-10">
          {sent ? (
            <div className="py-16 text-center">
              <div className="overline-v2">Thank you</div>
              <div className="mt-4 font-serif-warm text-4xl">Your enquiry has reached the atelier.</div>
              <p className="mt-4 text-[15px] text-[#4a4640] font-light">A designer will write to you within one working day. A copy is on its way to your email.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <L t="Your name"><input data-testid="v2-custom-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={I} /></L>
              <L t="Phone"><input data-testid="v2-custom-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} className={I} /></L>
              <L t="Email" span2><input type="email" data-testid="v2-custom-email" value={f.email} onChange={(e) => set("email", e.target.value)} className={I} /></L>
              <L t="City & country" span2><input value={f.city_country} onChange={(e) => set("city_country", e.target.value)} className={I} placeholder="e.g. Bengaluru, India" /></L>
              <L t="Rug size"><select value={f.rug_size} onChange={(e) => set("rug_size", e.target.value)} className={I}><option value="">Select…</option>{SIZES.map((s) => <option key={s}>{s}</option>)}</select></L>
              <L t="Material preference"><select value={f.material} onChange={(e) => set("material", e.target.value)} className={I}><option value="">Select…</option>{MATERIALS.map((s) => <option key={s}>{s}</option>)}</select></L>
              <L t="Room"><select value={f.room_type} onChange={(e) => set("room_type", e.target.value)} className={I}><option value="">Select…</option>{ROOMS.map((s) => <option key={s}>{s}</option>)}</select></L>
              <L t="Budget"><select value={f.budget_range} onChange={(e) => set("budget_range", e.target.value)} className={I}><option value="">Select…</option>{BUDGETS.map((s) => <option key={s}>{s}</option>)}</select></L>
              <L t="Preferred colours" span2><input value={f.preferred_colors} onChange={(e) => set("preferred_colors", e.target.value)} className={I} placeholder="e.g. muted olive, cream, a touch of terracotta" /></L>
              <L t="Required by" span2><input type="date" value={f.required_by} onChange={(e) => set("required_by", e.target.value)} className={I} /></L>
              <L t="Notes / inspiration" span2><textarea rows={4} value={f.notes} onChange={(e) => set("notes", e.target.value)} className={I} placeholder="Tell us about the room, the mood, or a memory you'd like the rug to hold." /></L>
              <L t="Upload inspiration image (optional)" span2>
                <div className="mt-2 flex items-center gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="v2-custom-upload" className="v2-btn-outline text-[11px]">
                    <Upload size={13} /> Choose image
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
                  {fileName && <span className="text-[12px] text-[#4a4640]">{fileName}</span>}
                </div>
              </L>
              <div className="md:col-span-2 mt-4">
                <button type="submit" disabled={loading} data-testid="v2-custom-submit" className="v2-btn-primary w-full justify-center">
                  {loading ? <><Loader2 className="animate-spin" size={14} /> Sending…</> : <>Send Enquiry <ArrowRight size={14} /></>}
                </button>
              </div>
            </div>
          )}
        </form>
      </section>
    </div>
  );
}

const I = "mt-2 w-full border-0 border-b border-[#c9c0ae] bg-transparent py-3 text-[15px] focus:border-[#1a1a1a] outline-none";
function L({ t, span2, children }) {
  return (
    <label className={`block ${span2 ? "md:col-span-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#7a7466]">{t}</span>
      {children}
    </label>
  );
}
